import React, { useEffect, useMemo, useState } from 'react';
import { geoNaturalEarth1, geoPath, geoCentroid, geoGraticule10 } from 'd3-geo';
import { Loader2 } from 'lucide-react';

const W = 960;
const H = 470;
const LEG = [['#FFD24A', 'Server pubblici rilevati'], ['#3E9BFF', 'Server propri dichiarati'], ['#FF8A3D', 'Server dei fornitori']];

export function useWorld() {
  const [geo, setGeo] = useState(null);
  useEffect(() => { fetch('/geo/world.json').then((r) => r.json()).then(setGeo).catch(() => setGeo({ features: [] })); }, []);
  return geo;
}

function Node({ p, color, title, i }) {
  return (
    <g transform={`translate(${p[0]},${p[1]})`} data-testid={`map-node-${i}`}>
      <circle r={6} fill="none" stroke={color} strokeWidth={1}><animate attributeName="r" values="4;16;4" dur="2.8s" begin={`${(i % 6) * 0.35}s`} repeatCount="indefinite" /><animate attributeName="opacity" values="0.9;0;0.9" dur="2.8s" begin={`${(i % 6) * 0.35}s`} repeatCount="indefinite" /></circle>
      <circle r={3.6} fill={color} style={{ filter: `drop-shadow(0 0 5px ${color})` }} /><title>{title}</title>
    </g>
  );
}

const arc = (a, b) => { const mx = (a[0] + b[0]) / 2; const my = (a[1] + b[1]) / 2 - Math.hypot(b[0] - a[0], b[1] - a[1]) * 0.25; return `M${a[0]},${a[1]} Q${mx},${my} ${b[0]},${b[1]}`; };

export default function InfraMap({ servers, sites, geo }) {
  const proj = useMemo(() => (geo ? geoNaturalEarth1().fitSize([W, H], geo) : null), [geo]);
  const byIso = useMemo(() => Object.fromEntries((geo?.features || []).map((f) => [f.id, f])), [geo]);
  if (!geo) return <div className="soc-card mt-4 min-h-[300px] grid place-items-center"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;
  const path = geoPath(proj);
  const used = new Set(sites.map((s) => s.country));
  const pub = servers.filter((s) => s.lat != null).map((s) => ({ p: proj([s.lng, s.lat]), c: '#FFD24A', t: `${s.ip} · ${s.hosts.join(', ')} · ${[s.city, s.country].filter(Boolean).join(', ')} · ${s.provider || ''}` }));
  const decl = sites.filter((s) => byIso[s.country]).map((s, i) => { const c = proj(geoCentroid(byIso[s.country])); return { p: [c[0] + (i % 3) * 7, c[1] + Math.floor(i / 3) * 7], c: s.kind === 'own' ? '#3E9BFF' : '#FF8A3D', t: `${s.name}${s.provider ? ` · ${s.provider}` : ''} · ${s.city || ''} ${s.country_name || ''}` }; });
  const hq = decl.find((d) => d.c === '#3E9BFF')?.p || (byIso.ITA ? proj(geoCentroid(byIso.ITA)) : null);
  const nodes = [...decl, ...pub];
  return (
    <div className="soc-card mt-4 overflow-hidden" data-testid="console-map">
      <div className="px-4 pt-4 pb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="soc-title">Geolocalizzazione infrastruttura · {nodes.length} nodi</p>
        <div className="flex flex-wrap gap-4 text-[10px] soc-mono soc-muted">{LEG.map(([c, l]) => <span key={l} className="inline-flex items-center gap-1.5"><i className="w-2 h-2 rounded-full" style={{ background: c, boxShadow: `0 0 6px ${c}` }} />{l.toUpperCase()}</span>)}</div>
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" style={{ background: '#070A10' }} data-testid="console-map-svg">
          <path d={path(geoGraticule10())} fill="none" stroke="rgba(0,240,255,0.07)" strokeWidth={0.5} />
          {geo.features.map((f) => <path key={f.id || f.properties.name} d={path(f)} fill={used.has(f.id) ? '#16304A' : '#111A26'} stroke="rgba(0,240,255,0.22)" strokeWidth={0.35} />)}
          {hq && nodes.map((n, i) => <path key={`a${i}`} d={arc(hq, n.p)} fill="none" stroke={n.c} strokeOpacity={0.55} strokeWidth={1} className="soc-dash" />)}
          {nodes.map((n, i) => <Node key={i} i={i} p={n.p} color={n.c} title={n.t} />)}
        </svg>
        <div className="soc-scan" />
      </div>
    </div>
  );
}
